import Link from "@components/Link";
import { ROUTES } from "@frontend/constants/routes";
import { Facebook, Instagram, LinkedIn, X, YouTube } from "@mui/icons-material";
import { Box, Container, Grid, Typography } from "@mui/material";
import { PropsWithChildren } from "react";
import ContentFooterSocialIcon from "./ContentFooterSocialIcon";

type LinkProps = PropsWithChildren<{ href: string; external?: boolean }>;

const SECTIONS = [
  {
    title: "Rate Education",
    children: [
      { href: "/rates", title: "Understanding Residential Rates", icon: null },
      { href: "/rates/resource-center", title: "Resource Center", icon: null },
    ],
  },
  {
    title: "Contractors & Developers",
    children: [
      {
        title: "PowerReady Eligibility Assessment",
        href: ROUTES.PowerReady.Eligibility.ROOT,
        icon: null,
      },
      {
        title: "PowerReady Incentive Estimator",
        href: ROUTES.PowerReady.Projects.ROOT,
        icon: null,
      },
      {
        title: "DAC Screener",
        href: ROUTES.PowerReady.DacScreenerPage,
        icon: null,
      },
      {
        title: "LMI Eligibility",
        href: ROUTES.PowerReady.LmiEligibility,
        icon: null,
      },
      { title: "Contact Us", href: ROUTES.PowerReady.Contact, icon: null },
    ],
  },
  {
    title: "Commercial & Industrial",
    children: [
      {
        title: "Understanding the Basics",
        href: ROUTES.Rates.CommercialIndustrial.Basics,
        icon: null,
      },
      {
        title: "Demand Charges",
        href: ROUTES.Rates.CommercialIndustrial.DemandCharges,
        icon: null,
      },
      {
        title: "Smart Usage Rewards",
        href: ROUTES.Rates.CommercialIndustrial.DemandResponse,
        icon: null,
      },
    ].filter(Boolean) as { href: string; title: string; icon: null }[],
  },
  {
    title: "Resources",
    children: [
      {
        href: "https://www.coned.com/en/accounts-billing/your-bill/how-to-read-your-bill",
        title: "How to Read Your Bill",
        icon: null,
      },
      {
        href: "https://www.coned.com/en/accounts-billing/your-bill/how-to-read-your-bill/understanding-your-bill-faq",
        title: "Frequently Asked Questions: About Bills",
        icon: null,
      },
      {
        href: "https://www.coned.com/en/save-money/shop-for-energy-service-companies",
        title: "Shop for Energy Service Companies",
        icon: null,
      },
      {
        href: "https://www.coned.com/en/accounts-billing/your-bill/how-to-read-your-bill/sample-bill-residential",
        title: "Sample Bill - Residential",
        icon: null,
      },
      {
        href: "https://www.coned.com/en/save-money/rebates-incentives-tax-credits",
        title: "Save With Rebates and Energy Saving Incentives",
        icon: null,
      },
      {
        href: "https://www.coned.com/en/accounts-billing/payment-plans-assistance",
        title: "Payment Plans and Assistance",
        icon: null,
      },
    ],
  },
  {
    title: "Social",
    children: [
      {
        title: (
          <ContentFooterSocialIcon Component={Facebook} text={"FACEBOOK"} />
        ),
        href: "https://www.facebook.com/ConEdison",
        icon: "",
      },
      {
        title: (
          <ContentFooterSocialIcon Component={LinkedIn} text={"LINKEDIN"} />
        ),
        href: "https://www.linkedin.com/company/con-edison/",
        icon: "",
      },
      {
        title: <ContentFooterSocialIcon Component={X} text={"X"} />,
        href: "https://x.com/conedison",
        icon: "",
      },
      {
        title: <ContentFooterSocialIcon Component={YouTube} text={"YOUTUBE"} />,
        href: "https://www.youtube.com/user/ConEdisonNY",
        icon: "",
      },
      {
        title: (
          <ContentFooterSocialIcon Component={Instagram} text={"INSTAGRAM"} />
        ),
        href: "https://www.instagram.com/conedison/?hl=en",
        icon: "",
      },
    ],
  },
];

const TopLink = ({ href, children, external }: LinkProps) => (
  <Link
    href={href}
    external={external}
    sx={{
      color: "grey.900",
      fontSize: "0.75rem",
      fontWeight: 600,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    }}
  >
    {children}
  </Link>
);

export default function ContentFooter() {
  return (
    <Box
      component="footer"
      sx={{
        borderTop: "1px solid",
        borderColor: "grey.700",
      }}
    >
      <Box
        sx={{
          bgcolor: "grey.200",
        }}
      >
        <Container>
          <Box
            sx={{
              pt: 12,
              pb: 10,
              px: { xs: 4, sm: 10 },
              gap: 8,
            }}
          >
            <Grid
              container
              spacing={4}
              sx={{
                justifyContent: { xs: undefined, md: "space-between" },
              }}
            >
              {SECTIONS.map((section) => [
                <Grid size={{ xs: 12, md: 4 }} key={section.title}>
                  <Box>
                    <Typography
                      variant="h6"
                      component={"p"}
                      sx={{ fontFamily: "Lexend", fontWeight: 700, mb: 2 }}
                    >
                      {section.title}
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ paddingInlineStart: 0, listStyleType: "none" }}
                    >
                      {section.children.map(
                        ({ href, title, icon: Icon }, i, array) => (
                          <Box
                            component="li"
                            key={typeof title === "string" ? title : href}
                            sx={{
                              mb: i === array.length - 1 ? undefined : 1.5,
                            }}
                          >
                            <TopLink
                              external={href.startsWith("http")}
                              href={href}
                            >
                              {Icon ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                  }}
                                >
                                  <Icon />
                                  {title}
                                </Box>
                              ) : (
                                title
                              )}
                            </TopLink>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Box>
                </Grid>,
              ])}
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
